import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Download, FileIcon, Lock } from 'lucide-react';
import { StandardButton } from '@/styles/StandardButtons';
import { waitForInitialSession, verifySessionIsLive, signOutLocally } from '@/lib/auth-session';
import { loginPathWithNext } from '@/lib/return-path';
import { fetchProtectedFile, ProtectedFileError, saveBlob, type DocumentKind, type ProtectedFile } from '@/lib/file-urls';

/**
 * The permanent page of a document: `/drive/document/:id` (Drive) and `/attachments/:id` (a record's file). The address never expires and carries no
 * credentials. No session -> Login, which brings the person back here; with a session the backend decides again, on this request, whether this person may
 * open this document (company, project, assignment, permissions). The bytes come through KrewUp and are shown from a local blob, so the address bar stays
 * on the KrewUp domain. Being signed in grants nothing by itself: without access the page is "Access denied" -- the same screen for a document that does
 * not exist, so it says nothing about what exists.
 */
const INLINE = [/^image\/(png|jpe?g|gif|webp|avif|bmp)$/i, /^application\/pdf$/i, /^video\//i, /^audio\//i, /^text\/plain$/i];
const previewKind = (contentType: string): 'image' | 'frame' | 'video' | 'audio' | null => {
  const type = contentType.split(';')[0].trim();
  if (!INLINE.some((re) => re.test(type))) return null;
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';
  return 'frame';
};

type State =
  | { status: 'checking' }
  | { status: 'loading' }
  | { status: 'ready'; file: ProtectedFile; objectUrl: string }
  | { status: 'denied' }
  | { status: 'error'; message: string };

export function DocumentViewerPage({ kind }: { kind: DocumentKind }) {
  const { id = '' } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ status: 'checking' });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    const controller = new AbortController();
    const toLogin = () => navigate(loginPathWithNext(`${location.pathname}${location.search}`), { replace: true });
    (async () => {
      setState({ status: 'checking' });
      const session = await waitForInitialSession();
      if (cancelled) return;
      if (!session) return toLogin(); // not signed in: Login, and Login comes back to this exact address
      setState({ status: 'loading' });
      try {
        const file = await fetchProtectedFile(kind, id, { signal: controller.signal });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(file.blob);
        document.title = file.fileName;
        setState({ status: 'ready', file, objectUrl });
      } catch (error) {
        if (cancelled || (error as Error)?.name === 'AbortError') return;
        if (error instanceof ProtectedFileError && error.isSignedOut) {
          // authenticated-fetch already renewed the token and retried once. A 401 alone is not "the session is over" (the backend answers it for a token it could not
          // check too): only Supabase saying so sends the person to Login -- and then the dead session is cleared first, or Login would just send them back here.
          const liveness = await verifySessionIsLive();
          if (cancelled) return;
          if (liveness === 'invalid') {
            await signOutLocally();
            return toLogin();
          }
          setState({ status: 'error', message: "We couldn't confirm your session right now. Please try again in a moment." });
          return;
        }
        if (error instanceof ProtectedFileError && error.isAccessDenied) setState({ status: 'denied' });
        else setState({ status: 'error', message: error instanceof Error ? error.message : 'The file could not be loaded.' });
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [kind, id, location.pathname, location.search, reloadKey, navigate]);

  const retry = useCallback(() => setReloadKey((n) => n + 1), []);

  if (state.status === 'checking' || state.status === 'loading') {
    return <Centered><p className="text-sm text-gray-500" role="status">Loading document…</p></Centered>;
  }
  if (state.status === 'denied') {
    return (
      <Centered>
        <div className="max-w-md text-center" data-testid="document-access-denied">
          <Lock className="mx-auto mb-4 h-10 w-10 text-gray-400" />
          <h1 className="mb-2 text-xl font-semibold">Access denied</h1>
          <p className="mb-6 text-sm text-gray-600">You don&apos;t have access to this document, or it is no longer available. If you think this is a mistake, ask the person who shared it with you.</p>
          <StandardButton customVariant="white" onClick={() => window.location.assign('/')}>Go to KrewUp</StandardButton>
        </div>
      </Centered>
    );
  }
  if (state.status === 'error') {
    return (
      <Centered>
        <div className="max-w-md text-center">
          <h1 className="mb-2 text-xl font-semibold">We couldn&apos;t open this document</h1>
          <p className="mb-6 text-sm text-gray-600">{state.message}</p>
          <StandardButton customVariant="white" onClick={retry}>Try again</StandardButton>
        </div>
      </Centered>
    );
  }

  const { file, objectUrl } = state;
  const preview = previewKind(file.contentType);
  return (
    <div className="flex h-screen flex-col bg-gray-50">
      <header className="flex items-center justify-between gap-3 border-b bg-white px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <FileIcon className="h-5 w-5 flex-shrink-0 text-gray-500" />
          <h1 className="truncate text-sm font-medium" title={file.fileName}>{file.fileName}</h1>
        </div>
        <StandardButton customVariant="white" onClick={() => saveBlob(file.blob, file.fileName)}>
          <Download className="mr-2 h-4 w-4" />Download
        </StandardButton>
      </header>
      <main className="flex min-h-0 flex-1 items-center justify-center p-4">
        {preview === 'image' && <img src={objectUrl} alt={file.fileName} className="max-h-full max-w-full object-contain" />}
        {preview === 'frame' && <iframe src={objectUrl} title="Document preview" className="h-full w-full rounded border bg-white" />}
        {preview === 'video' && <video src={objectUrl} controls className="max-h-full max-w-full" />}
        {preview === 'audio' && <audio src={objectUrl} controls />}
        {!preview && (
          <div className="text-center" data-testid="document-no-preview">
            <FileIcon className="mx-auto mb-3 h-12 w-12 text-gray-300" />
            <p className="mb-4 text-sm text-gray-600">This file type can&apos;t be previewed. You can download it.</p>
          </div>
        )}
      </main>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6">{children}</div>;
}

export const DriveDocumentViewerPage = () => <DocumentViewerPage kind="drive" />;
export const AttachmentViewerPage = () => <DocumentViewerPage kind="attachment" />;
